import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TagsSelect from '../TagsSelect';
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useParams } from "react-router-dom";
import { hashDiagnosticId, useDiagnosticRender } from "../../services/frontendDiagnostics";
import useTickets from "../../hooks/useTickets";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.paper,
  },
  tab: {
    minWidth: 120,
    width: 120,
  },

  ticketOptionsBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    background: theme.palette.background.paper,
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },

  optionsTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },

  leftTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },

  optionsBottomRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },

  tagsFilterWrapper: {
    flex: 1,
    minWidth: 0,
    width: "100%",
  },

  queueSelectWrapper: {
    flex: 1,
    minWidth: 0,
    width: "100%",
  },

  serachInputWrapper: {
    flex: 1,
    background: theme.palette.background.default,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
    minWidth: 0,
  },
  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    minWidth: 0,
  },
}));

const TicketsManager = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [searchParam, setSearchParam] = useState("");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [showFavoriteTickets, setShowFavoriteTickets] = useState(false);
  const [selectedQueueIds, setSelectedQueueIds] = useState(
    user?.queues?.map(q => q.id) || []
  );
    
  const [showOnlyIgnored, setShowOnlyIgnored] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);

  const searchInputRef = useRef();
  const searchTimeoutRef = useRef(null);

  const { ticketId } = useParams();
  const ticketSeleccionadoId = ticketId ? +ticketId : null;

  const selectedQueueIdsParam = useMemo(
    () => JSON.stringify(selectedQueueIds),
    [selectedQueueIds]
  );
  const selectedTagIdsParam = useMemo(
    () => selectedTags?.map(t => t.id).join(",") || "",
    [selectedTags]
  );

  const { count: openTicketsCount } = useTickets({
    pageNumber: 1,
    status: "open",
    showAll: showAllTickets,
    isFavorite: showFavoriteTickets,
    queueIds: selectedQueueIdsParam,
    tagIds: selectedTagIdsParam,
    countOnly: true,
  });

  const { count: pendingTicketsCount } = useTickets({
    pageNumber: 1,
    status: "pending",
    showAll: showAllTickets,
    isFavorite: showFavoriteTickets,
    queueIds: selectedQueueIdsParam,
    tagIds: selectedTagIdsParam,
    countOnly: true,
  });

  useDiagnosticRender("TicketsManager", () => ({
    ticketIdHash: hashDiagnosticId(ticketId),
    counts: {
      selectedQueues: selectedQueueIds.length,
      selectedTags: selectedTags.length,
      showAll: showAllTickets ? 1 : 0,
      showFavorites: showFavoriteTickets ? 1 : 0,
      openCount: openTicketsCount,
      pendingCount: pendingTicketsCount
    },
    metadata: {
      tab,
      tabOpen
    }
  }));
    
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const { data } = await api.get("/tags");
        setAllTags(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    if (["ADMIN", "SUPERADMIN"].includes(user?.profile.toUpperCase())) {
      setShowAllTickets(true);
    }
  }, [user]);

  useEffect(() => {
    if (tab !== "search") {
      setShowOnlyIgnored(false);
    }
  }, [tab]);
                                         
  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleChangeTab = (e, newValue) => setTab(newValue);
  const handleChangeTabOpen = (e, newValue) => setTabOpen(newValue);

  const applyPanelStyle = (listStatus) =>
    tabOpen !== listStatus ? { width: 0, height: 0 } : {};

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />

      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value="open"
            icon={<MoveToInboxIcon />}
            label={i18n.t("tickets.tabs.open.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value="closed"
            icon={<CheckBoxIcon />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value="search"
            icon={<SearchIcon />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>

      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        <div className={classes.optionsTopRow}>
          <div className={classes.leftTop}>
            {tab === "search" ? (
              <>
                <div className={classes.serachInputWrapper}>
                  <SearchIcon className={classes.searchIcon} />
                  <InputBase
                    className={classes.searchInput}
                    inputRef={searchInputRef}
                    placeholder={i18n.t("tickets.search.placeholder")}
                    type="search"
                    onChange={handleSearch}
                  />
                </div>
                <FormControlLabel
                  label="Solo Ignorados"
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={showOnlyIgnored}
                      onChange={() => setShowOnlyIgnored(prev => !prev)}
                      name="showOnlyIgnored"
                      color="primary"
                    />
                  }
                />
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setNewTicketModalOpen(true)}
                >
                  {i18n.t("ticketsManager.buttons.newTicket")}
                </Button>

                <FormControlLabel
                  label="Favoritos"
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={showFavoriteTickets}
                      onChange={() => setShowFavoriteTickets(prev => !prev)}
                      name="showFavoriteTickets"
                      color="primary"
                    />
                  }
                />

                <Can
                  role={user.profile}
                  perform="tickets-manager:showall"
                  yes={() => (
                    <FormControlLabel
                      label={i18n.t("tickets.buttons.showAll")}
                      labelPlacement="start"
                      control={
                        <Switch
                          size="small"
                          checked={showAllTickets}
                          onChange={() => setShowAllTickets(prev => !prev)}
                          name="showAllTickets"
                          color="primary"
                        />
                      }
                    />
                  )}
                />
              </>
            )}
          </div>
        </div>

        {! (tab === "search") && (
          <div className={classes.optionsBottomRow}>
            <div className={classes.tagsFilterWrapper}>
              <TagsSelect
                fullWidth
                tags={allTags}
                value={selectedTags}
                onChange={(newValue) => setSelectedTags(newValue)}
              />
            </div>

            <div className={classes.queueSelectWrapper}>
              <TicketsQueueSelect
                selectedQueueIds={selectedQueueIds}
                userQueues={user?.queues}
                onChange={(values) => setSelectedQueueIds(values)}
                style={{ width: "100%" }}
              />
            </div>
          </div>
        )}
      </Paper>

      {/* OPEN / PENDING Tabs */}
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
                <Badge className={classes.badge} badgeContent={openTicketsCount} color="primary">
                {i18n.t("ticketsList.assignedHeader")}
              </Badge>
            }
            value="open"
          />
          <Tab
            label={
                <Badge className={classes.badge} badgeContent={pendingTicketsCount} color="secondary">
                {i18n.t("ticketsList.pendingHeader")}
              </Badge>
            }
            value="pending"
          />
        </Tabs>
        <Paper className={classes.ticketsWrapper}>
          {tabOpen === "open" && (
            <TicketsList
              listStatus="open"
              showAll={showAllTickets}
              showFavoriteTickets={showFavoriteTickets}
              selectedQueueIds={selectedQueueIds}
              selectedTags={selectedTags}
              selectedTicketId={ticketSeleccionadoId}
              style={applyPanelStyle("open")}
            />
          )}
          {tabOpen === "pending" && (
            <TicketsList
              listStatus="pending"
              showAll={showAllTickets}
              showFavoriteTickets={showFavoriteTickets}
              selectedQueueIds={selectedQueueIds}
              selectedTags={selectedTags}
              selectedTicketId={ticketSeleccionadoId}
              style={applyPanelStyle("pending")}
            />
          )}
        </Paper>
      </TabPanel>

      {/* CLOSED Tab */}
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          listStatus="closed"
          showAll={true}
          showFavoriteTickets={showFavoriteTickets}
          selectedQueueIds={selectedQueueIds}
          selectedTags={selectedTags}     
          selectedTicketId={ticketSeleccionadoId}
        />
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          showFavoriteTickets={showFavoriteTickets}
          selectedQueueIds={selectedQueueIds}
          selectedTicketId={ticketSeleccionadoId}
          showOnlyIgnored={showOnlyIgnored} 
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;
